/**
 * Maps failed requests - FHIR `OperationOutcome` payloads, bare HTTP statuses
 * and transport failures - onto plain Australian English messages, keeping the
 * raw payload for the expandable technical section.
 *
 * The mapping is an ordered pattern table plus a generic fallback, so a shape
 * nobody anticipated still produces copy a clinician can read (Principle IV,
 * FR-059, SC-006). No message here may name a status code, a URL, a FHIR
 * element or an expression: that content belongs in `detail` only.
 *
 * @author John Grimes
 */

import type { PlainError } from "shared/api";

/** Everything the caller learned about a failed request. */
export type ErrorSource = {
  /** HTTP status, absent when no response arrived at all. */
  status?: number;
  /** HTTP status text, when the transport supplied one. */
  statusText?: string;
  /** Parsed JSON body, when the response carried JSON. */
  body?: unknown;
  /** Raw body text, when the response was not JSON. */
  text?: string;
  /** The underlying thrown value, for transport failures. */
  cause?: unknown;
};

/** The failure kinds the pattern table can recognise. */
export type PlainErrorKey =
  | "network"
  | "notAServer"
  | "unreadable"
  | "formatNotSupported"
  | "resourceTypeUnknown"
  | "notSupported"
  | "unauthorised"
  | "forbidden"
  | "notFound"
  | "conflict"
  | "invalid"
  | "timeout"
  | "serverFault"
  | "unknown";

/**
 * The user-visible copy for each failure kind. Every entry is a complete
 * sentence in plain Australian English and says what the user can do next.
 */
export const PLAIN_ERROR_MESSAGES: Record<PlainErrorKey, string> = {
  network:
    "We couldn't reach the server. Check your internet connection and try again.",
  notAServer:
    "That address doesn't look like a server this app can use. Check the address and try again.",
  unreadable:
    "The server's reply couldn't be understood, so nothing has changed.",
  formatNotSupported:
    "This server can't produce that file format. Choose a different format and try again.",
  resourceTypeUnknown:
    "This server doesn't hold that kind of record, so this view can't run here.",
  notSupported: "This server doesn't support what was asked for.",
  unauthorised:
    "Your access to this server has expired. Sign in to the server again to carry on.",
  forbidden: "You don't have permission to do that on this server.",
  notFound: "That item isn't on the server any more. It may have been deleted.",
  conflict:
    "Someone else changed this view since you opened it. Reload it to see their version before saving.",
  invalid:
    "The server wouldn't accept this because some of the details are invalid.",
  timeout:
    "The server took too long to answer. This is usually temporary, so try again shortly.",
  serverFault:
    "The server ran into a problem and couldn't finish. This is usually temporary, so try again shortly.",
  unknown:
    "Something went wrong on the server and the request couldn't be completed.",
};

/** What the pattern table matches against. */
type ErrorContext = {
  /** True when anything at all came back: a status, a body or body text. */
  readonly hasResponse: boolean;
  /** HTTP status, absent when no response arrived. */
  readonly status?: number;
  /** True when a JSON body was parsed, however unhelpful. */
  readonly hasBody: boolean;
  /** Body text, when the response was not JSON. */
  readonly text?: string;
  /** The issue the server considers most serious, when there is one. */
  readonly issue?: fhir4.OperationOutcomeIssue;
  /** Lower-cased diagnostics and details text of that issue, for matching. */
  readonly diagnostics: string;
};

/** A single row of the pattern table; returns its key only when it matches. */
type ErrorPattern = (context: ErrorContext) => PlainErrorKey | undefined;

/** Failure kind implied by an `OperationOutcome` issue code. */
const KEY_BY_ISSUE_CODE: Partial<
  Record<fhir4.OperationOutcomeIssue["code"], PlainErrorKey>
> = {
  security: "unauthorised",
  login: "unauthorised",
  expired: "unauthorised",
  forbidden: "forbidden",
  suppressed: "forbidden",
  "not-found": "notFound",
  deleted: "notFound",
  conflict: "conflict",
  duplicate: "conflict",
  invalid: "invalid",
  structure: "invalid",
  required: "invalid",
  value: "invalid",
  invariant: "invalid",
  "code-invalid": "invalid",
  "business-rule": "invalid",
  timeout: "timeout",
  exception: "serverFault",
  transient: "serverFault",
  "lock-error": "serverFault",
  "not-supported": "notSupported",
  "no-store": "notSupported",
};

/** Failure kind implied by an HTTP status on its own. */
const KEY_BY_STATUS: Record<number, PlainErrorKey> = {
  401: "unauthorised",
  403: "forbidden",
  404: "notFound",
  408: "timeout",
  409: "conflict",
  412: "conflict",
  422: "invalid",
  504: "timeout",
};

/** Lowest status treated as the server's own fault. */
const SERVER_FAULT_STATUS = 500;

/** A body that begins a markup document rather than data. */
const HTML_BODY = /^\s*<(?:!doctype|html|\?xml)/i;

/** Diagnostics naming an output format. */
const MENTIONS_FORMAT = /\b_?formats?\b/;

/** Diagnostics saying that something is unavailable on this server. */
const MENTIONS_UNSUPPORTED =
  /\bunsupported\b|\bnot\s+(?:supported|available|implemented)\b/;

/** Diagnostics naming a resource type the server does not hold. */
const UNKNOWN_RESOURCE_TYPE =
  /\b(?:unknown|unsupported|unrecognised|unrecognized|invalid)\s+resource\s+type\b|\bresource\s+type\b[^.]*\bnot\s+(?:supported|known|recognised|recognized|valid)\b/;

/**
 * Narrows an arbitrary body to an `OperationOutcome`, without trusting it.
 *
 * @param body - The parsed JSON body of the response.
 * @returns The outcome, or `undefined` when the body is something else.
 */
const asOperationOutcome = (
  body: unknown,
): fhir4.OperationOutcome | undefined => {
  if (
    typeof body !== "object" ||
    body === null ||
    !("resourceType" in body) ||
    body.resourceType !== "OperationOutcome" ||
    !("issue" in body) ||
    !Array.isArray(body.issue)
  ) {
    return undefined;
  }
  // The resource type and the issue list are checked above; individual issue
  // fields are still read defensively below.
  return body as fhir4.OperationOutcome;
};

/**
 * Reads a body the backend already rendered into plain language, so that the
 * app repeats the backend's wording rather than second-guessing it.
 *
 * @param body - The parsed JSON body of the response.
 * @returns The supplied plain error, or `undefined` when there is none.
 */
const asSuppliedPlainError = (body: unknown): PlainError | undefined => {
  if (typeof body !== "object" || body === null || !("message" in body)) {
    return undefined;
  }
  const { message } = body;
  if (typeof message !== "string" || message.trim() === "") {
    return undefined;
  }
  const detail = "detail" in body ? body.detail : undefined;
  return typeof detail === "string" ? { message, detail } : { message };
};

// The ordered pattern table. Earlier rows are more specific than later ones:
// transport before payload, payload shape before issue code, issue code before
// bare status.
const ERROR_PATTERNS: readonly ErrorPattern[] = [
  // Nothing came back at all, so the request never reached a server.
  ({ hasResponse }) => (hasResponse ? undefined : "network"),

  // A markup document means the address is serving pages, not data.
  ({ text, hasBody }) =>
    !hasBody && text !== undefined && HTML_BODY.test(text)
      ? "notAServer"
      : undefined,

  // Some other body that would not parse as JSON.
  ({ text, hasBody }) =>
    !hasBody && text !== undefined && text.trim() !== ""
      ? "unreadable"
      : undefined,

  // The view's resource type is not one this server holds (FR-019).
  ({ diagnostics }) =>
    UNKNOWN_RESOURCE_TYPE.test(diagnostics) ? "resourceTypeUnknown" : undefined,

  // The server refused the requested export format (FR-055).
  ({ diagnostics }) =>
    MENTIONS_FORMAT.test(diagnostics) && MENTIONS_UNSUPPORTED.test(diagnostics)
      ? "formatNotSupported"
      : undefined,

  // The server said precisely what went wrong.
  ({ issue }) =>
    issue && Object.hasOwn(KEY_BY_ISSUE_CODE, issue.code)
      ? KEY_BY_ISSUE_CODE[issue.code]
      : undefined,

  // Only the status is left to go on.
  ({ status }) => (status === undefined ? undefined : KEY_BY_STATUS[status]),
  ({ status }) =>
    status !== undefined && status >= SERVER_FAULT_STATUS
      ? "serverFault"
      : undefined,
];

/**
 * Builds the matching context once, so each pattern stays a cheap predicate.
 *
 * @param source - The failure to describe.
 * @returns The context the pattern table matches against.
 */
const toContext = (source: ErrorSource): ErrorContext => {
  const outcome = asOperationOutcome(source.body);
  // The first fatal or error issue explains the failure best; warnings and
  // information issues are noise beside it.
  const issue =
    outcome?.issue.find(
      (candidate) =>
        candidate.severity === "fatal" || candidate.severity === "error",
    ) ?? outcome?.issue[0];
  const diagnostics = [issue?.diagnostics, issue?.details?.text]
    .filter((part): part is string => typeof part === "string")
    .join(" ")
    .toLowerCase();
  const hasBody = source.body !== undefined;
  const hasText = source.text !== undefined && source.text.trim() !== "";
  return {
    hasResponse: source.status !== undefined || hasBody || hasText,
    status: source.status,
    hasBody,
    text: source.text,
    issue,
    diagnostics,
  };
};

/**
 * Classifies a failure against the pattern table.
 *
 * @param source - Everything known about the failed request.
 * @returns The key of the first matching pattern, or `unknown` when none match.
 *
 * @example
 * ```ts
 * classifyError({ status: 404 }); // "notFound"
 * ```
 */
export const classifyError = (source: ErrorSource): PlainErrorKey => {
  const context = toContext(source);
  for (const pattern of ERROR_PATTERNS) {
    const key = pattern(context);
    if (key !== undefined) {
      return key;
    }
  }
  return "unknown";
};

/**
 * Assembles the technical section: status line, raw payload and, for transport
 * failures, the thrown value. Payloads are kept verbatim (SC-006).
 *
 * @param source - Everything known about the failed request.
 * @returns The detail text, or `undefined` when nothing technical is known.
 */
const formatDetail = (source: ErrorSource): string | undefined => {
  const parts: string[] = [];
  if (source.status !== undefined) {
    parts.push(
      source.statusText === undefined || source.statusText === ""
        ? `HTTP ${String(source.status)}`
        : `HTTP ${String(source.status)} ${source.statusText}`,
    );
  }
  if (source.body !== undefined) {
    parts.push(JSON.stringify(source.body, undefined, 2));
  } else if (source.text !== undefined && source.text.trim() !== "") {
    parts.push(source.text);
  }
  if (source.cause !== undefined) {
    parts.push(
      source.cause instanceof Error
        ? `${source.cause.name}: ${source.cause.message}`
        : JSON.stringify(source.cause),
    );
  }
  return parts.length > 0 ? parts.join("\n\n") : undefined;
};

/**
 * Turns a failed request into the message the user sees and the detail behind
 * the technical disclosure. A body the backend already wrote in plain language
 * is passed through as-is; anything else goes through the pattern table.
 *
 * @param source - Everything known about the failed request.
 * @returns The plain error to render.
 *
 * @example
 * ```ts
 * toPlainError({ status: 504, body: outcome });
 * // { message: "The server took too long to answer. ...", detail: "HTTP 504\n\n{...}" }
 * ```
 */
export const toPlainError = (source: ErrorSource): PlainError => {
  const detail = formatDetail(source);
  const supplied = asSuppliedPlainError(source.body);
  if (supplied) {
    return supplied.detail === undefined && detail !== undefined
      ? { message: supplied.message, detail }
      : supplied;
  }
  const message = PLAIN_ERROR_MESSAGES[classifyError(source)];
  return detail === undefined ? { message } : { message, detail };
};
